import { Settings, Trash2 } from "lucide-react"
import type { LoadBalancerConfig } from '@/features/fmo'
import { isPercentageValid } from "../utils/validation"

interface LoadBalancerConfigCardProps {
  config: LoadBalancerConfig
  onConfigure: (config: LoadBalancerConfig) => void
  onDelete: (configId: string) => void
}

export default function LoadBalancerConfigCard({ 
  config, 
  onConfigure, 
  onDelete 
}: LoadBalancerConfigCardProps) {
  const totalPercentage = config.assignedLinks.reduce((sum, link) => sum + link.percentage, 0)
  const isValid = isPercentageValid(config.assignedLinks)

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className={`w-3 h-3 rounded-full ${isValid ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{config.organizationName}</h3>
            <p className="text-sm text-gray-600">Type: {config.organizationType}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isValid 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {totalPercentage}% {isValid ? 'Valid' : 'Invalid'}
          </div>
          <button
            onClick={() => onConfigure(config)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Configure
          </button>
          <button
            onClick={() => onDelete(config.id)}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {config.assignedLinks.map((link) => (
          <div key={link.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">
                {link.vendorIntake?.vendorName || 'Unknown Vendor'}
              </span>
              <span className="text-sm font-bold text-blue-600">{link.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${link.percentage}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
